import type { AssignmentTarget } from "@/store/types";
import type { DispatchTableRow } from "./dispatchTableRow";
import { dispatchTagKey } from "./projectDispatchRecord";

/** The workspace tag naming a driver's Driver Manager book, read for the picker's first entry. */
export const DRIVER_MANAGER_TAG_SLUG = "driver-manager";

/** One entry in the reassignment picker. */
export type ReassignOption = {
  target: AssignmentTarget;
  label: string;
  /** Second line: "Driver Manager", "Team", "Member", "You". */
  role: string;
  preselected: boolean;
};

/** The one Driver Manager code every row shares, or `null` when a row has none or two rows differ. */
function sharedDriverManagerCode(rows: readonly DispatchTableRow[], dmTagSlug: string): string | null {
  const key = dispatchTagKey(dmTagSlug);
  let shared: string | null = null;
  for (const row of rows) {
    const value = row.record[key];
    const code = (Array.isArray(value) ? value[0] : value)?.trim();
    if (!code) return null;
    if (shared !== null && shared !== code) return null;
    shared = code;
  }
  return shared;
}

/**
 * What the reassignment picker lists for the rows it was opened on, in order.
 *
 * The Driver Manager code from the rows' `dmTagSlug` tag when every row shares one, preselected; "Me"; every view but
 * the current one as a team; then the members alphabetically, the viewer left out. `query` keeps the entries whose
 * label holds it, ignoring case.
 *
 * @param input.dmTagSlug - The tag holding the Driver Manager code; the literal {@link DRIVER_MANAGER_TAG_SLUG} today.
 * @param input.me - The viewer's membership id.
 * @param input.views - Every Dispatch view in the cascade, by id and name.
 * @param input.members - The office members who may take a handoff, as `useOfficeMembers` lists them.
 */
export function reassignOptionsFor(input: {
  rows: readonly DispatchTableRow[];
  dmTagSlug: string;
  me: string;
  views: readonly { id: string; name: string }[];
  currentViewId: string;
  members: readonly { membershipId: string; name: string }[];
  query: string;
}): ReassignOption[] {
  const options: ReassignOption[] = [];
  const code = input.rows.length > 0 ? sharedDriverManagerCode(input.rows, input.dmTagSlug) : null;
  if (code !== null) {
    options.push({
      target: { kind: "dm", code },
      label: `Driver Manager · ${code}`,
      role: "Driver Manager",
      preselected: true,
    });
  }
  options.push({ target: { kind: "member", membershipId: input.me }, label: "Me", role: "You", preselected: false });
  for (const view of input.views) {
    if (view.id === input.currentViewId) continue;
    options.push({ target: { kind: "team", viewId: view.id }, label: view.name, role: "Team", preselected: false });
  }
  const members = input.members
    .filter((member) => member.membershipId !== input.me)
    .sort((a, b) => a.name.localeCompare(b.name));
  for (const member of members) {
    options.push({
      target: { kind: "member", membershipId: member.membershipId },
      label: member.name,
      role: "Member",
      preselected: false,
    });
  }
  const query = input.query.trim().toLowerCase();
  return query === "" ? options : options.filter((option) => option.label.toLowerCase().includes(query));
}
